import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";
import { SEOImage } from "../../../../components/seo/SEOImage";

export const TeleprompterApresentacoesES = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter para PowerPoint y Presentaciones: Oratoria Profesional
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Domina tus diapositivas y conquista a tu audiencia con total confianza. <strong>Sincroniza invisiblemente tu guion</strong> con PowerPoint, Google Slides o Keynote usando PromptNinja.
        </p>

        <p className="text-slate-300 mb-8 leading-relaxed">
            En esta guía definitiva para oradores competentes, mostramos cómo PromptNinja se convierte en tu socio estratégico para presentaciones de alto impacto. Aprende a leer tu guion con naturalidad, manteniendo contacto visual con la audiencia y evitando el miedo a quedarse en blanco en momentos críticos. Descubre técnicas para integrar el teleprompter con cualquier software de diapositivas, asegurando que presentes con fluidez, autoridad y profesionalismo, ya sea en un escenario físico o en reuniones virtuales. Transforma tu oratoria y destácate con presentaciones memorables y sin titubeos.
        </p>

        <SEOImage
            slug="scripts-para-aulas"
            src="scripts-for-teacher-lessons.webp"
            alt="Profesor usando teleprompter para clases online"
            caption="PromptNinja ayuda a los educadores a mantener el ritmo y el contacto visual durante las clases grabadas o en vivo."
            width={1200}
            height={675}
        />

        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12 shadow-xl">
            <h3 className="text-2xl font-bold text-white mb-4">Presenta con Confianza Estilo TED</h3>
            <p className="text-slate-300 mb-6 font-medium">
                Transforma tus diapositivas en apoyo visual y a ti mismo en la estrella de la presentación.
                <strong>PromptNinja</strong> sincroniza invisiblemente tu guion para la audiencia.
            </p>
            <a href="/?lang=es#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Iniciar Presentación con PromptNinja
            </a>
            <p className="mt-4 text-sm text-slate-400 font-medium">Confianza estilo TED al instante.</p>
        </div>


        <div className="bg-gradient-to-r from-red-900/30 to-green-900/30 p-8 rounded-xl border border-slate-700 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">Presentación: Memorizar vs Teleprompter PRO</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/70 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> Método Tradicional (Memorizar)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📚 <strong>Prep:</strong> 2 semanas memorizando guion</li>
                        <li>🧠 <strong>Estrés:</strong> Miedo a olvidar estadística crítica</li>
                        <li>😰 <strong>En vivo:</strong> Mente en blanco en slide 15 = nervios</li>
                        <li>📄 <strong>Backup:</strong> Papel en mesa (mirar ABAJO = perder contacto)</li>
                        <li>⏱️ <strong>Resultado:</strong> Parece inseguro, "eaahhh", "uuuhhh" frecuentes</li>
                    </ul>
                </div>
                <div className="bg-slate-900/70 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> PromptNinja + PowerPoint
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📚 <strong>Prep:</strong> 10 minutos pegando guion</li>
                        <li>🧠 <strong>Estrés:</strong> Cero (guion siempre visible SOLO para ti)</li>
                        <li>😌 <strong>En vivo:</strong> Lees fluido = confiado</li>
                        <li>📱 <strong>Backup:</strong> Laptop/tablet que solo TÚ ves</li>
                        <li>⏱️ <strong>Resultado:</strong> "Domina el tema", cero vacilación</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Teleprompter = presentador confiado SIN meses de memorización.
            </p>
        </div>

        <SEOImage
            slug="scripts-para-aulas"
            src="scripts-for-presentation-classes.webp"
            alt="Presentación de diapositivas con teleprompter integrado"
            caption="Sincroniza tus diapositivas con tu guion y nunca más pierdas el hilo en tus presentaciones."
            width={1200}
            height={675}
        />

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">3 Setups de Presentación Profesional</h3>
        <div className="space-y-6 mb-12">
            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-cyan-500">
                <h3 className="text-xl font-bold text-cyan-400 mb-3">Setup #1: Auditorio Presencial (Sala de Conferencias)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop en podio + proyector mostrando PPT.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Proyector:</strong> Muestra PowerPoint pantalla completa (audiencia ve slides)</li>
                    <li><strong>Laptop (tú):</strong> "Vista de Presentador" PowerPoint en un monitor</li>
                    <li><strong>Tablet al lado:</strong> PromptNinja con guion completo</li>
                    <li>Miras a audiencia, lees discretamente de tablet (al lado de laptop)</li>
                    <li>Audiencia ve: tú mirando "al frente" (realmente leyendo tablet invisible)</li>
                    <li>Usa marcador [SLIDE X] en guion = saber cuándo avanzar PPT</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal para:</strong> Pitches inversores, presentaciones directiva, conferencias.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-400 mb-3">Setup #2: Zoom/Teams Online (Híbrido)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> PC/Mac + webcam + presentación online.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Zoom:</strong> Compartir Pantalla → selecciona SOLO PowerPoint (no "Pantalla Completa")</li>
                    <li><strong>Monitor 1:</strong> PowerPoint en Modo Presentador (slides + notas privadas)</li>
                    <li><strong>Monitor 2:</strong> PromptNinja pantalla completa DEBAJO de webcam</li>
                    <li>Zoom muestra: PowerPoint. Tú ves: PowerPoint + PromptNinja</li>
                    <li>Lees del teleprompter mirando "a la webcam" = contacto visual perfecto</li>
                    <li>Audiencia online: solo ve slides + tu cara confiada</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal para:</strong> Webinars, reuniones con clientes, capacitación online.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-red-400 mb-3">Setup #3: Google Slides + Control Remoto Móvil</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop + teléfono (control dual).
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Laptop:</strong> Google Slides presentando + PromptNinja ventana lateral</li>
                    <li><strong>Teléfono #1:</strong> Remoto Google Slides (avanza diapositivas)</li>
                    <li><strong>Teléfono #2:</strong> Remoto P2P PromptNinja (controla scroll guion)</li>
                    <li>Tú controlas: slides con mano derecha, teleprompter con izquierda</li>
                    <li>Sincronía perfecta: avanzo slide = pauso teleprompter = explico visual</li>
                    <li>Reanudo teleprompter = continúo narrativa</li>
                </ol>
                <p className="text-yellow-400 text-sm mt-3">⚠️ <strong>Tip Pro:</strong> Usa comando [STOP] en guion = pausa auto en cada slide.</p>
            </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg my-8 border-l-4 border-orange-500">
            <h3 className="text-2xl font-bold text-white mb-4">Técnica "Segundo Cerebro" (Slides Limpios + Guion Rico)</h3>
            <p className="text-slate-300 mb-4">
                <strong>ERROR CLÁSICO:</strong> Slides LLENOS de texto porque te da miedo olvidar. Audiencia lee slide = te IGNORA a ti hablando.
            </p>
            <p className="text-slate-300 mb-4">
                <strong>MÉTODO PRO:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-300 text-sm">
                <li><strong>Slides:</strong> SOLO imágenes/gráficos impactantes + título (max 10 palabras)</li>
                <li><strong>PromptNinja:</strong> Guion COMPLETO con estadísticas, argumentos, transiciones</li>
                <li>Tú hablas: datos ricos del teleprompter. Audiencia ve: visual limpio slide</li>
                <li>100% atención en TI (no leyendo slide como robot)</li>
            </ul>
            <p className="text-green-400 text-sm mt-4">
                ✅ <strong>Resultado:</strong> Presentación estilo TED. Slides = apoyo visual. Tú = estrella.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Cómo Sincronizar Diapositivas con Guion</h3>
        <div className="bg-slate-800 p-6 rounded-lg my-6">
            <SEOContentHowTo
                title=""
                schemaTitle="Cómo Sincronizar Teleprompter y Diapositivas"
                totalTime="PT5M"
                tools={["PromptNinja", "PowerPoint o Google Slides"]}
                steps={[
                    {
                        title: "Paso 1: Estructura Guion por Slide",
                        text: "Copia el texto de cada diapositiva en el guion. Pon [SLIDE 1], [SLIDE 2] al inicio de cada bloque para saber cuándo cambiar."
                    },
                    {
                        title: "Paso 2: Navega por las diapositivas",
                        text: "Puedes navegar por las diapositivas de tu guion usando los atajos de flecha: '→' para avanzar y '←' para retroceder."
                    },
                    {
                        title: "Paso 3: Usa Comandos STOP para Pausas",
                        text: "Inserta [STOP] después de cada marcador de slide. Teleprompter hace pausa automática = tú explicas visual del slide antes de continuar guion. O usa comando [PAUSE X] para pausar X segundos."
                    },
                    {
                        title: "Paso 4: Ensayo con Temporizador",
                        text: "Primer pase: marca tiempo REAL de cada slide. Ajusta guion si algún slide es muy largo/corto. Meta: +/- 15% de lo planeado."
                    }
                ]}
            />
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">5 Errores Fatales en Presentación con Teleprompter</h3>
        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #1: Compartir Pantalla COMPLETA en Zoom (Expone Teleprompter)</h4>
                <p className="text-slate-300 text-sm">
                    Zoom → Compartir Pantalla → "Pantalla Completa". Audiencia VE todo: PowerPoint + PromptNinja abierto + tus pestañas Chrome. Vergonzoso.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> Comparte SOLO ventana PowerPoint. PromptNinja queda fuera del share = invisible para audiencia.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #2: Guion Desincronizado con Slides (Orden Erróneo)</h4>
                <p className="text-slate-300 text-sm">
                    PowerPoint slide 10. Guion teleprompter = texto del slide 8. Hablas datos viejos mientras slide muestra gráfico diferente. Audiencia confundida.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> SIEMPRE prueba ANTES. Avanza slides manual mientras lees guion = verifica sincro. Ajusta marcadores [SLIDE X].</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #3: Leer "Como Robot" (Sin Variación Tonal)</h4>
                <p className="text-slate-300 text-sm">
                    Usas teleprompter pero lees monótono como GPS. Audiencia duerme en 3min. Pareces poco sincero incluso sabiendo contenido.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> Marca ÉNFASIS en guion (MAYÚSCULAS, negrita, **resaltado**). Pausa 2s en comas importantes. ENSAYA variar tono.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #4: Laptop/Tablet Muy Lejos (Movimiento Ocular Obvio)</h4>
                <p className="text-slate-300 text-sm">
                    Teleprompter a 2 metros a la IZQUIERDA. Presentas girando cabeza 60º para leer. Audiencia SABE que lees. Pierdes credibilidad.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> Tablet/laptop MÁXIMO 30cm de donde miras naturalmente. Presencial: al lado de podio. Online: bajo webcam.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #5: Sin Backup si Laptop Falla</h4>
                <p className="text-slate-300 text-sm">
                    Presentación en vivo. Laptop crashea (¡¿update de Windows?!). Teleprompter se fue. NO memorizaste = improvisas HORRIBLE. Cliente nota.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> PromptNinja en celular también (sincro QR). Laptop falla = tomas celular discreto, sigues leyendo. Audiencia ni nota.</span>
                </p>
            </div>
        </div>

        <h3 className="text-2xl font-bold text-white mt-8 mb-4">Tips Específicos PowerPoint/Keynote</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">📊 Vista de Presentador</h3>
                <p className="text-sm text-slate-300">PowerPoint/Keynote tiene "Vista de Presentador": ves notas privadas, audiencia ve solo slides. Combina CON teleprompter = notas visuales + guion completo.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">🎨 Slides Minimalistas</h3>
                <p className="text-sm text-slate-300">Con teleprompter, los slides pueden tener CERO texto. Solo imagen impactante. Tú hablas rico de guion, visual limpio.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">⏱️ Auto-Avance Slides</h3>
                <p className="text-sm text-slate-300">PowerPoint puede auto-avanzar slides cada Xmin. COMBINA con comando [WAIT Xs] en teleprompter = sincro automática perfecta.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">📱 Clicker Bluetooth</h3>
                <p className="text-sm text-slate-300">Clicker avanza PPT slides (mano der). Celular controla teleprompter (mano izq). Control total sin tocar laptop.</p>
            </div>
        </div>

        <div className="text-center bg-slate-800 p-8 rounded-lg my-10">
            <h3 className="text-3xl font-bold text-white mb-4">🎤 Presentaciones Memorables Sin Memorizar</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Slides impactantes + guion invisible = presentador confiado que "domina el tema".
            </p>
            <a
                href="https://promptninja.solutionkit.com.br"
                className="inline-block bg-gradient-to-r from-orange-500 to-red-600 font-bold py-3 px-8 rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                🚀 Preparar Presentación (Gratis)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Sincroniza con PowerPoint • Control remoto • Invisible a audiencia</p>
        </div>

        <SEOContentFAQ
            title="Preguntas Frecuentes - Presentaciones PowerPoint/Keynote"
            items={[
                {
                    question: "¿La audiencia notará que leo de teleprompter?",
                    answer: "NO si posicionas bien. Online: teleprompter bajo webcam = miras 'a cámara'. Presencial: laptop/tablet al lado del podio = miras discreto. Secreto: no FIJES ojos en texto 100% tiempo. Mira audiencia, vuelve a texto, repite natural."
                },
                {
                    question: "¿Funciona con Modo Presentador de PowerPoint?",
                    answer: "¡PERFECTAMENTE! Modo Presentador muestra: (1) slide actual, (2) slide sig, (3) notas, (4) reloj. PromptNinja = guion COMPLETO en segunda pantalla. Tienes: visual slide + notas cortas + guion detallado. Triple respaldo."
                },
                {
                    question: "¿Cómo sincronizo slides con guion teleprompter?",
                    answer: "Usa marcadores [SLIDE 1], [SLIDE 2] en MAYÚSCULAS en guion. Cuando ves [SLIDE 5], avanzas PowerPoint manual (clicker o flecha). O usa comando [STOP] = teleprompter pausa, avanzas slide, reanudas lectura."
                },
                {
                    question: "¿Puedo usar en reunión Zoom compartiendo pantalla?",
                    answer: "¡SÍ! Crítico: comparte SOLO ventana PowerPoint (no Pantalla Completa). PromptNinja queda abierto fuera de share = tú ves, audiencia NO ve. Prueba ANTES: inicia Zoom, comparte, mira preview = confirma teleprompter es invisible."
                },
                {
                    question: "¿Necesito internet durante presentación o funciona offline?",
                    answer: "Funciona 100% OFFLINE tras cargado. Presentación en auditorio sin WiFi = OK. Guion guardado localmente. Solo necesita internet si: (1) primera carga o (2) control remoto celular (usa WiFi local pero puedes crear hotspot)."
                },
                {
                    question: "¿Cómo evitar que PowerPoint oscurezca pantalla en presentación?",
                    answer: "Windows: Configuración → Sistema → Energía → Apagar pantalla = Nunca (durante pres). Mac: Preferencias Sistema → Ahorro Energía → desactivar reposo. O mantén laptop enchufada = suele evitar oscurecimiento."
                },
                {
                    question: "¿Puedo usar teleprompter para Q&A después de slides?",
                    answer: "¡SÍ! Prepara sección guion aparte: 'FAQ PREPARADO'. Lista 10-15 preguntas probables + respuestas. Durante Q&A: busca pregunta similar en teleprompter (Ctrl+F), lees respuesta preparada = parece improvisado pero es PERFECTO."
                },
                {
                    question: "¿Y si quiero improvisar parte de la presentación (no leer todo)?",
                    answer: "Guion = GUÍA, no camisa de fuerza. Marca secciones [AD-LIB] donde improvisas. O usa colores: verde = leer exacto (estadísticas críticas), amarillo = parafrasear libre. Teleprompter = red de seguridad, no prisión."
                }
            ]}
        />
    </>
);
